import React, { createContext, useContext, useEffect, useLayoutEffect, useRef } from 'react';

/**
 * Shares a detached DOM node that the placement is portaled into.
 * PlacementSlot moves this node into the results list when it mounts.
 */
const PlacementPortalContainerContext = createContext<HTMLDivElement | null>(null);

export const usePlacementPortalContainer = (): HTMLDivElement | null =>
	useContext(PlacementPortalContainerContext);

export const PlacementPortalContainerProvider: React.Provider<HTMLDivElement | null> = PlacementPortalContainerContext.Provider;

/**
 * Module-level flag that tracks whether the search page has claimed ownership
 * of the placement portal. When the search page initialises its portal container
 * first, Quick Find's fetch-results should not render its own banner to avoid
 * duplicate placements.
 */
let searchPagePlacementClaimed = false;

export const setSearchPagePlacementClaimed = (claimed: boolean): void => {
	searchPagePlacementClaimed = claimed;
};

export const isSearchPagePlacementClaimed = (): boolean => searchPagePlacementClaimed;

/**
 * Moves the pre-mounted placement container into this position in the DOM.
 * When `trackImpressionOnMount` is true, fires the impression event on mount.
 */
export const PlacementSlot = ({
	trackImpressionOnMount,
}: {
	trackImpressionOnMount?: boolean;
} = {}): React.JSX.Element | null => {
	const slotRef = useRef<HTMLDivElement>(null);
	const placementContainer = usePlacementPortalContainer();
	const trackImpression = useTrackImpression();

	useLayoutEffect(() => {
		const slot = slotRef.current;
		if (slot && placementContainer) {
			placementContainer.style.display = '';
			slot.appendChild(placementContainer);
		}
		return () => {
			if (placementContainer) {
				placementContainer.style.display = 'none';
				placementContainer.remove();
			}
		};
	}, [placementContainer]);

	useEffect(() => {
		if (trackImpressionOnMount) {
			trackImpression?.();
		}
	}, [trackImpressionOnMount, trackImpression]);

	return <div ref={slotRef} />;
};

/**
 * Shares the trackImpression callback captured from the PostOffice placement's onMessageViewed.
 * This allows OAuthContainer to trigger the "viewed" event when the banner becomes visible.
 */
const TrackImpressionContext = createContext<(() => void) | null>(null);

export const useTrackImpression = (): (() => void) | null => useContext(TrackImpressionContext);

export const TrackImpressionProvider: React.Provider<(() => void) | null> = TrackImpressionContext.Provider;
